import {
  type FC,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import type { ChangelogProductId } from 'src/components/docs/Changelog/products';
import { PRODUCTS } from 'src/components/docs/Changelog/products';

export const CHANGELOG_PRODUCTS_QUERY_KEY = 'products';
export const CHANGELOG_PRODUCTS_STORAGE_KEY = 'docs_changelog_products';

const VALID_IDS: ReadonlySet<ChangelogProductId> = new Set(
  PRODUCTS.map((p) => {
    return p.id;
  }),
);

const parseIds = (raw: string | null | undefined): Set<ChangelogProductId> => {
  const result = new Set<ChangelogProductId>();
  if (raw == null || raw === '') return result;
  for (const part of raw.split(',')) {
    const trimmed = part.trim() as ChangelogProductId;
    if (trimmed.length > 0 && VALID_IDS.has(trimmed)) {
      result.add(trimmed);
    }
  }
  return result;
};

const serializeIds = (ids: Set<ChangelogProductId>): string => {
  return Array.from(ids).join(',');
};

const readQueryValue = (
  value: string | string[] | undefined,
): string | undefined => {
  if (Array.isArray(value)) return value.join(',');
  return value;
};

export type ChangelogFilterValue = {
  activeProducts: Set<ChangelogProductId>;
  setActiveProducts: (next: Set<ChangelogProductId>) => void;
  isHydrated: boolean;
};

const EMPTY_SET = new Set<ChangelogProductId>();

const FALLBACK_VALUE: ChangelogFilterValue = {
  activeProducts: EMPTY_SET,
  setActiveProducts: () => {},
  isHydrated: false,
};

const ChangelogFilterContext = createContext<ChangelogFilterValue | null>(null);

export const useChangelogFilter = (): ChangelogFilterValue => {
  const ctx = useContext(ChangelogFilterContext);
  return ctx ?? FALLBACK_VALUE;
};

/**
 * Page-scoped provider. Reads the URL filter exactly once and broadcasts
 * to all ChangelogEntry / ChangelogSection / ChangelogFilter descendants
 * so they share one `useRouter` subscription instead of one per shortcode
 * instance.
 */
export const ChangelogFilterProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const router = useRouter();
  const [isHydrated, setIsHydrated] = useState<boolean>(false);

  const queryValue = readQueryValue(router.query[CHANGELOG_PRODUCTS_QUERY_KEY]);

  const activeProducts = useMemo<Set<ChangelogProductId>>(() => {
    if (!isHydrated) return EMPTY_SET;
    return parseIds(queryValue);
  }, [queryValue, isHydrated]);

  useEffect(() => {
    if (!router.isReady || isHydrated) return;

    if (queryValue == null || queryValue === '') {
      let stored: string | null = null;
      try {
        stored = window.localStorage.getItem(CHANGELOG_PRODUCTS_STORAGE_KEY);
      } catch {
        // localStorage may be unavailable (private mode); ignore.
      }
      const restored = parseIds(stored);
      if (restored.size > 0) {
        // Defer flipping `isHydrated` until the URL actually carries the
        // restored filter, otherwise activeProducts is briefly EMPTY_SET
        // and all entries flash visible for one frame before the filter
        // applies.
        const nextQuery = { ...router.query };
        nextQuery[CHANGELOG_PRODUCTS_QUERY_KEY] = serializeIds(restored);
        router
          .replace(
            {
              pathname: router.pathname,
              query: nextQuery,
              hash: window.location.hash,
            },
            undefined,
            { scroll: false, shallow: true },
          )
          .then(() => {
            setIsHydrated(true);
          });
        return;
      }
    }

    setIsHydrated(true);
  }, [router, queryValue, isHydrated]);

  const setActiveProducts = useCallback(
    (next: Set<ChangelogProductId>): void => {
      const serialized = serializeIds(next);

      try {
        if (serialized === '') {
          window.localStorage.removeItem(CHANGELOG_PRODUCTS_STORAGE_KEY);
        } else {
          window.localStorage.setItem(
            CHANGELOG_PRODUCTS_STORAGE_KEY,
            serialized,
          );
        }
      } catch {
        // Ignore storage errors; URL remains authoritative.
      }

      const nextQuery = { ...router.query };
      if (serialized === '') {
        delete nextQuery[CHANGELOG_PRODUCTS_QUERY_KEY];
      } else {
        nextQuery[CHANGELOG_PRODUCTS_QUERY_KEY] = serialized;
      }

      router.replace(
        {
          pathname: router.pathname,
          query: nextQuery,
          hash:
            typeof window === 'undefined' ? undefined : window.location.hash,
        },
        undefined,
        { scroll: false, shallow: true },
      );
    },
    [router],
  );

  const value = useMemo<ChangelogFilterValue>(() => {
    return { activeProducts, setActiveProducts, isHydrated };
  }, [activeProducts, setActiveProducts, isHydrated]);

  return (
    <ChangelogFilterContext.Provider value={value}>
      {children}
    </ChangelogFilterContext.Provider>
  );
};
